import { useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { FileText, Search, ArrowRight, Loader2, Mail, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { useLanguage } from "@/i18n/LanguageContext";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MyLettersPage = () => {
  const { t } = useLanguage();
  const [email, setEmail] = useState("");
  const [letters, setLetters] = useState([]);
  const [hasPaid, setHasPaid] = useState(false);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  const searchLetters = useCallback(async () => {
    if (!email) {
      toast.error("Inserisci la tua email");
      return;
    }

    setLoading(true);
    setSearched(true);
    try {
      const response = await axios.get(`${API}/letters/patient/${encodeURIComponent(email)}`);
      setLetters(response.data.letters || []);
      setHasPaid(response.data.has_paid || false);
      
      if (response.data.letters?.length === 0) {
        toast.info("Nessuna lettera trovata per questa email");
      } else if (!response.data.has_paid) {
        toast.info("Per scaricare le lettere in PDF, attiva un abbonamento");
      }
      
      // Store email in sessionStorage for letter page
      sessionStorage.setItem('patientEmail', email);
    } catch (error) {
      console.error("Error fetching letters:", error);
      toast.error("Errore nella ricerca delle lettere");
    } finally {
      setLoading(false);
    }
  }, [email]);

  const handleSubmit = (e) => {
    e.preventDefault();
    searchLetters();
  };

  return (
    <div className="min-h-screen bg-stone-50" data-testid="my-letters-page">
      <Header />
      <main className="pt-32 pb-24 px-6">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <span className="text-xs tracking-[0.2em] uppercase text-[#8a9a86] font-medium">
              {t("letters.label")}
            </span>
            <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-stone-900 tracking-tight leading-tight mt-4 mb-6">
              {t("letters.title")}
            </h1>
            <p className="text-stone-600 leading-relaxed max-w-xl mx-auto">
              {t("letters.subtitle")}
            </p>
          </div>

          {/* Search Form */}
          <form onSubmit={handleSubmit} className="max-w-md mx-auto mb-12">
            <div className="flex gap-3">
              <div className="relative flex-1">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t("pricing.emailLabel")}
                  className="w-full pl-12 pr-4 py-4 rounded-full border border-stone-200 focus:outline-none focus:ring-2 focus:ring-[#2c4c3b]/20 text-sm"
                  data-testid="email-search-input"
                />
              </div>
              <Button
                type="submit"
                disabled={loading}
                className="bg-[#2c4c3b] hover:bg-[#1a2e22] text-stone-50 rounded-full px-6"
                data-testid="search-letters-btn"
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <Search className="w-5 h-5" />
                )}
              </Button>
            </div>
          </form>

          {/* Subscription Status Banner */}
          {searched && !hasPaid && letters.length > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 mb-8 flex items-start gap-4">
              <Lock className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-medium text-amber-900 mb-1">{t("letters.notActive")}</h3>
                <p className="text-amber-700 text-sm mb-3">
                  {t("letters.notActiveText")}
                </p>
                <Link to="/pricing">
                  <Button size="sm" className="bg-amber-600 hover:bg-amber-700 text-white rounded-full">
                    {t("letters.discoverPlans")}
                  </Button>
                </Link>
              </div>
            </div>
          )}

          {/* Letters List */}
          {searched && (
            <div className="space-y-4" data-testid="letters-list">
              {letters.length > 0 ? (
                letters.map((letter) => (
                  <Link
                    key={letter.id}
                    to={`/lettera/${letter.id}`}
                    className="block bg-white rounded-2xl p-6 shadow-[0_4px_20px_rgba(0,0,0,0.04)] border border-stone-100 hover:shadow-[0_8px_30px_rgba(0,0,0,0.08)] hover:-translate-y-0.5 transition-all group"
                    data-testid={`letter-card-${letter.id}`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 rounded-xl bg-[#e8ece6] flex items-center justify-center flex-shrink-0">
                          <FileText className="w-5 h-5 text-[#2c4c3b]" />
                        </div>
                        <div>
                          <h3 className="font-serif text-lg text-stone-900 group-hover:text-[#2c4c3b] transition-colors">
                            {letter.title}
                          </h3>
                          <p className="text-sm text-[#8a9a86] mt-1">{letter.month}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {!hasPaid && (
                          <Lock className="w-4 h-4 text-stone-300" />
                        )}
                        <ArrowRight className="w-5 h-5 text-stone-300 group-hover:text-[#2c4c3b] group-hover:translate-x-1 transition-all flex-shrink-0" />
                      </div>
                    </div>
                  </Link>
                ))
              ) : (
                <div className="text-center py-12">
                  <FileText className="w-16 h-16 text-stone-200 mx-auto mb-4" />
                  <p className="text-stone-500">{t("letters.noLetters")}</p>
                  <p className="text-sm text-stone-400 mt-2">
                    {t("letters.checkEmail")}
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Info Box */}
          {!searched && (
            <div className="bg-[#e8ece6] rounded-2xl p-8 text-center max-w-lg mx-auto">
              <FileText className="w-12 h-12 text-[#2c4c3b] mx-auto mb-4" />
              <h3 className="font-serif text-xl text-stone-900 mb-2">
                {t("letters.waiting")}
              </h3>
              <p className="text-stone-600 text-sm">
                {t("letters.waitingText")}
              </p>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default MyLettersPage;
